import * as React from "react";
import { Container } from "react-bootstrap";
import { shallowEqual } from "react-redux";
import {
  LineChart,
  XAxis,
  YAxis,
  ReferenceLine,
  CartesianGrid,
  Label,
  Line,
  Legend,
  Tooltip,
} from "recharts";

import { ChartErrorSvg } from "../ChartErrorSvg";
import { LoaderContainer } from "../../Utilities/LoaderContainer";

import { useAppSelector } from "../../../redux/store";
import { tidyFrequencyCurve } from "../../../libs/chartLib";
import { FrequencyCurve } from "../../../Types/Common/OscillometryTypes";

const { useState, useEffect, useRef } = React;

const COLORS = {
  resistance: "#3C89E8",
  reactance: "#1E5AA8",
  grid: "#e0e0e0",
  axis: "#555",
  axisLabel: "#444",
  reference: "#666",
  background: {
    start: "#fafafa",
    end: "#f5f5f5",
  },
  border: "#e8e8e8",
  shadow: "rgba(0, 0, 0, 0.08)",
  tooltip: {
    bg: "rgba(255, 255, 255, 0.98)",
    border: "#d0d0d0",
    text: "#333",
    muted: "#888",
  },
};

const styleId = "frequency-curve-chart-styles";
if (typeof document !== "undefined" && !document.getElementById(styleId)) {
  const styleSheet = document.createElement("style");
  styleSheet.id = styleId;
  styleSheet.textContent = `
    .fc-chart-container {
      animation: fcFadeIn 0.4s ease-out;
    }

    @keyframes fcFadeIn {
      from {
        opacity: 0;
        transform: translateY(4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .fc-tooltip {
      animation: fcTooltipIn 0.15s ease-out;
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }

    @keyframes fcTooltipIn {
      from {
        opacity: 0;
        transform: translateY(2px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    .fc-legend-item {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 6px 14px;
      background: rgba(255, 255, 255, 0.7);
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      color: #334155;
      letter-spacing: 0.01em;
      transition: background 0.15s ease;
    }

    .fc-legend-item:hover {
      background: rgba(255, 255, 255, 0.9);
    }

    .fc-legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.8), 0 1px 3px rgba(0, 0, 0, 0.1);
    }
  `;
  document.head.appendChild(styleSheet);
}

/**
 * Data point for chart - merged R and X by freq
 */
interface FrequencyCurveChartPoint {
  frequency: number;
  R: number | null;
  X: number | null;
  unit: string;
}

interface FrequencyCurveRanges {
  frequencyRange: [number, number];
  valueRange: [number, number];
}

interface FrequencyCurveTicks {
  xTicks: number[];
  yTicks: number[];
}

interface FrequencyCurveChartProps {
  frequencyCurve: FrequencyCurve | null | undefined;
  rejected?: boolean;
  width?: number;
  height?: number;
  small?: boolean;
  fontHeight?: number;
  id?: string;
  showLegend?: boolean;
  hideTooltip?: boolean;
}

export const FrequencyCurveChart: React.FC<FrequencyCurveChartProps> = ({
  frequencyCurve,
  rejected = false,
  width = 400,
  height = 300,
  small = false,
  fontHeight = 14,
  id = "frequency-curve-chart",
  showLegend = true,
  hideTooltip = false,
}) => {
  const translationText = useAppSelector(
    (state) => state.translations,
    shallowEqual
  );

  const [chartData, setChartData] = useState<FrequencyCurveChartPoint[] | null>(
    null
  );
  const [errorState, setErrorState] = useState<"none" | "rejected" | "invalid">(
    "none"
  );

  const ranges = useRef<FrequencyCurveRanges | undefined>();
  const ticks = useRef<FrequencyCurveTicks>({
    xTicks: [],
    yTicks: [],
  });
  const unit = useRef<string>("cmH2O/(L/s)");

  useEffect(() => {
    if (!frequencyCurve) {
      setChartData(null);
      setErrorState(rejected ? "rejected" : "invalid");
      return;
    }

    setErrorState("none");
    try {
      const tidiedData = tidyFrequencyCurve(frequencyCurve);

      if (!tidiedData || !tidiedData.points || tidiedData.points.length === 0) {
        setErrorState("invalid");
        return;
      }

      ranges.current = tidiedData.ranges;
      ticks.current = tidiedData.ticks;
      unit.current = tidiedData.unit;
      setChartData(tidiedData.points);
      setErrorState("none");
    } catch (error) {
      console.error("Error processing frequency curve data:", error);
      setErrorState("invalid");
    }
  }, [frequencyCurve, rejected]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="fc-tooltip"
          style={{
            background: COLORS.tooltip.bg,
            padding: "14px 18px",
            border: `1px solid ${COLORS.tooltip.border}`,
            borderRadius: "10px",
            boxShadow: `0 4px 20px ${COLORS.shadow}, 0 1px 3px rgba(0, 0, 0, 0.04)`,
            minWidth: "200px",
          }}
        >
          {/* Frequency header */}
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "6px",
              marginBottom: "12px",
              paddingBottom: "10px",
              borderBottom: `1px solid ${COLORS.grid}`,
            }}
          >
            <span
              style={{
                fontSize: "20px",
                fontWeight: 600,
                color: COLORS.tooltip.text,
                letterSpacing: "-0.02em",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {label}
            </span>
            <span
              style={{
                fontSize: "12px",
                fontWeight: 500,
                color: COLORS.tooltip.muted,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}
            >
              Hz
            </span>
          </div>

          {/* Data points */}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {payload.map((entry: any, index: number) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: entry.color,
                      boxShadow: `0 0 0 2px ${entry.color}20`,
                    }}
                  />
                  <span
                    style={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: COLORS.tooltip.muted,
                    }}
                  >
                    {entry.dataKey === "R" ? "Resistance" : "Reactance"}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: COLORS.tooltip.text,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {entry.value?.toFixed(2) ?? "---"}
                </span>
              </div>
            ))}
          </div>

          {/* Unit footer */}
          <div
            style={{
              marginTop: "10px",
              paddingTop: "8px",
              borderTop: `1px solid ${COLORS.grid}`,
              fontSize: "11px",
              fontWeight: 500,
              color: COLORS.tooltip.muted,
              textAlign: "right",
              letterSpacing: "0.02em",
            }}
          >
            {unit.current}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          marginTop: "16px",
        }}
      >
        <div className="fc-legend-item">
          <div
            className="fc-legend-dot"
            style={{ backgroundColor: COLORS.resistance }}
          />
          <span>{translationText?.resistance ?? "Resistance (R)"}</span>
        </div>
        <div className="fc-legend-item">
          <div
            className="fc-legend-dot"
            style={{ backgroundColor: COLORS.reactance }}
          />
          <span>{translationText?.reactance ?? "Reactance (X)"}</span>
        </div>
      </div>
    );
  };

  return (
    <Container
      className={`fc-chart-container ${small ? "FrequencyCurveChart-Small" : "FrequencyCurveChart"}`}
      style={{
        fontSize: fontHeight,
        padding: small ? "0px" : "16px 20px",
        background: `linear-gradient(180deg, ${COLORS.background.start} 0%, ${COLORS.background.end} 100%)`,
        borderRadius: small ? 4 : 12,
        border: `1px solid ${COLORS.border}`,
        boxShadow: `0 1px 3px ${COLORS.shadow}, 0 4px 12px ${COLORS.shadow}`,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)",
          borderRadius: "12px 12px 0 0",
          pointerEvents: "none",
        }}
      />

      {errorState !== "none" ? (
        errorState === "rejected" ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              width: "100%",
              minHeight: height,
            }}
          >
            <span
              className="text-danger"
              style={{
                fontSize: small ? 14 : 16,
                fontWeight: 500,
              }}
            >
              This session has been rejected.
            </span>
          </div>
        ) : (
          <ChartErrorSvg id={id} width={width} height={height} small={false} />
        )
      ) : chartData && ranges.current ? (
        <>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <LineChart
              id={id}
              width={width}
              height={height}
              data={chartData}
              margin={{
                top: 16,
                right: 28,
                bottom: 24,
                left: 20,
              }}
              style={{ cursor: small ? "pointer" : "" }}
            >
              <CartesianGrid
                stroke={COLORS.grid}
                strokeDasharray="3 3"
                opacity={0.7}
              />

              {/* X-Axis: Frequency (Hz) */}
              <XAxis
                dataKey="frequency"
                type="number"
                domain={ranges.current.frequencyRange}
                ticks={ticks.current.xTicks}
                interval={0}
                tick={{
                  fontSize: 11,
                  fill: COLORS.axis,
                  fontWeight: 500,
                }}
                stroke="#999"
                strokeWidth={1}
                tickLine={{ stroke: "#ccc" }}
                axisLine={{ stroke: "#999", strokeWidth: 1 }}
              >
                <Label
                  value={translationText?.frequency ?? "Frequency (Hz)"}
                  offset={-4}
                  position="bottom"
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    fill: COLORS.axisLabel,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                  }}
                />
              </XAxis>

              {/* Y-Axis: R/X Values */}
              <YAxis
                type="number"
                domain={ranges.current.valueRange}
                ticks={ticks.current.yTicks}
                interval={0}
                tick={{
                  fontSize: 11,
                  fill: COLORS.axis,
                  fontWeight: 500,
                }}
                stroke="#999"
                strokeWidth={1}
                tickLine={{ stroke: "#ccc" }}
                axisLine={{ stroke: "#999", strokeWidth: 1 }}
                width={48}
              >
                <Label
                  value={unit.current}
                  angle={-90}
                  position="insideLeft"
                  offset={12}
                  style={{
                    textAnchor: "middle",
                    fontSize: 10,
                    fontWeight: 600,
                    fill: COLORS.axisLabel,
                    letterSpacing: "0.02em",
                  }}
                />
              </YAxis>

              <ReferenceLine
                y={0}
                stroke={COLORS.reference}
                strokeWidth={1.5}
                strokeDasharray="0"
              />

              <Line
                type="monotone"
                dataKey="R"
                name={translationText?.resistance ?? "Resistance (R)"}
                stroke={COLORS.resistance}
                strokeWidth={2.5}
                dot={{
                  fill: COLORS.resistance,
                  stroke: "#fff",
                  strokeWidth: 2,
                  r: 4,
                }}
                activeDot={{
                  r: 6,
                  stroke: COLORS.resistance,
                  strokeWidth: 2,
                  fill: "#fff",
                }}
                connectNulls={true}
                isAnimationActive={false}
              />

              <Line
                type="monotone"
                dataKey="X"
                name={translationText?.reactance ?? "Reactance (X)"}
                stroke={COLORS.reactance}
                strokeWidth={2.5}
                dot={{
                  fill: COLORS.reactance,
                  stroke: "#fff",
                  strokeWidth: 2,
                  r: 4,
                }}
                activeDot={{
                  r: 6,
                  stroke: COLORS.reactance,
                  strokeWidth: 2,
                  fill: "#fff",
                }}
                connectNulls={true}
                isAnimationActive={false}
              />

              <Tooltip
                content={small || hideTooltip ? <></> : <CustomTooltip />}
                cursor={{
                  stroke: COLORS.reference,
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />
            </LineChart>
          </div>

          {showLegend && !small && <CustomLegend />}
        </>
      ) : (
        <LoaderContainer />
      )}
    </Container>
  );
};